import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Theme } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import FormInputComponent from 'webapp/components/form/input';

import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';

interface Args {
	model: DataSelect;
	theme: Theme | string;
	blockSetup?: BlockSetup;
}

const MIN_RECORD_LIMIT = 1;
const MAX_RECORD_LIMIT = 100;

export default class BlockSettingsMetricRecordLimitComponent extends Component<Args> {
	@tracked recordLimitString = '';
	@tracked errorMessage = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.updateRecordLimitStringFromModel();
	}

	get placeholder() {
		const visualization = this.args.blockSetup?.visualization;
		return visualization?.recordLimit || 'Unlimited';
	}

	get hasError() {
		return this.errorMessage.length > 0;
	}

	private validate(value: string): number | null {
		const trimmed = value.trim();

		// empty string is valid (means "Unlimited")
		if (trimmed === '') {
			this.errorMessage = '';
			return null;
		}

		const numericValue = +trimmed;

		if (!Number.isInteger(numericValue)) {
			this.errorMessage = 'Value must be a number';
			return null;
		}

		if (numericValue < MIN_RECORD_LIMIT || numericValue > MAX_RECORD_LIMIT) {
			this.errorMessage = `Value must be between ${MIN_RECORD_LIMIT} and ${MAX_RECORD_LIMIT}`;
			return null;
		}

		this.errorMessage = '';
		return numericValue;
	}

	@action
	updateRecordLimitStringFromModel(): void {
		this.recordLimitString = String(this.args.model.recordLimit || '');
	}

	@action
	changeRecordLimit(): void {
		const recordLimit = this.validate(this.recordLimitString);

		if (this.errorMessage) {
			return;
		}

		if (recordLimit !== null) {
			this.recordLimitString = String(recordLimit);
		}

		const ds = this.args.model;
		ds.recordLimit = recordLimit;

		const blockSetup = this.args.blockSetup;
		if (blockSetup?.isAutosaveOn) {
			blockSetup.saveDataSelect(ds);
		}
	}
	<template>
		<div
			class="row {{if this.hasError 'errorClass'}}"
			{{didUpdate this.updateRecordLimitStringFromModel @model}}
		>
			<label class="right--half">Max # of data values</label>
			<div class="right--half">
				<FormInputComponent
					maxlength="3"
					placeholder={{this.placeholder}}
					@loseFocusOnEnter={{true}}
					@value={{this.recordLimitString}}
					@focusOut={{this.changeRecordLimit}}
					@hideFloatLabel={{true}}
					@theme={{@theme}}
				/>
				{{#if this.errorMessage}}
					<span class="error-message">{{this.errorMessage}}</span>
				{{/if}}
			</div>
		</div>
	</template>
}
